import React, { useState, useEffect } from 'react'
import ReactDOM from 'react-dom';
import NotificationItem from '../NotificationItem'
import useVisibleNotificationsBatch from "../../../Hooks/useVisibleNotificationsBatch"
import { ReactComponent as Close } from "../../../assets/icons/notifications/Cross M.svg"
import { ReactComponent as NODATA } from "../../../assets/icons/no-data.svg";
import { markAllNotificationsAsRead, markNotificationAsRead } from "../../../Data/Functions/index"
import { initNotificationStatsQueue } from "../../../Data/notificationStatsQueue"
import { showCustomToast } from '../../../Components/shared/customToast';
import "./index.css"
import "../../../Screens/Account/TeamMembers.css"

const NotificationModal = ({ onClose, notifications: initialNotifications, userData, setHasUnread }) => {
    const [loading, setLoading] = useState(false);
    const [localNotifications, setLocalNotifications] = useState(initialNotifications);

    useEffect(() => {
        if (userData?.id) {
            initNotificationStatsQueue(userData.id);
        }
    }, [userData?.id]);

    useEffect(() => {
        setLocalNotifications(initialNotifications);
    }, [initialNotifications]);

    useEffect(() => {
        const hasUnreadNow = localNotifications.some(n => !n.readStatus?.isRead);
        setHasUnread(hasUnreadNow);
    }, [localNotifications, setHasUnread]);

    const updateNotificationAsRead = (id) => {
        const now = Date.now();
        setLocalNotifications(prev =>
            prev.map(n =>
                n.id === id
                    ? {
                        ...n,
                        readStatus: {
                            ...n.readStatus,
                            isRead: true,
                            readAt: now,
                        },
                    }
                    : n
            )
        );
    };

    const handleBatchSend = async (ids, userId) => {
        if (!ids.length || !userId) return;
                const now = Date.now();
                setLocalNotifications(prev =>
                    prev.map(n =>
                        ids.includes(n.id)
                            ? {
                                ...n,
                                readStatus: {
                                    ...n.readStatus,
                                    isRead: true,
                                    readAt: now,
                                },
                            }
                            : n
                    )
                );

        try {
            const data = await markNotificationAsRead(ids, userId);
            if (data?.success) {
                showCustomToast("Notifications marked as read", "success");
            } else {
                setLocalNotifications(prev =>
                    prev.map(n =>
                        ids.includes(n.id)
                            ? {
                                ...n,
                                readStatus: {
                                    ...n.readStatus,
                                    isRead: false,
                                    readAt: null,
                                },
                            }
                            : n
                    )
                );
                showCustomToast("Error marking notifications as read", "error");
            }
        } catch (error) {
            setLocalNotifications(prev =>
                prev.map(n =>
                    ids.includes(n.id)
                        ? {
                            ...n,
                            readStatus: {
                                ...n.readStatus,
                                isRead: false,
                                readAt: null,
                            },
                        }
                        : n
                )
            );
            console.error('Error marking notifications as read', error);
            showCustomToast("Error marking notifications as read", "error");
        }
    };

    const { containerRef } = useVisibleNotificationsBatch({
        userId: userData?.id,
        notifications: localNotifications,
        onBatchSend: handleBatchSend
    });

    const handleMarkAllAsRead = async () => {
        setLoading(true);
        const now = Date.now();
        const previousNotifications = localNotifications;
        setLocalNotifications(prev =>
            prev.map(n => ({
                ...n,
                readStatus: {
                    ...n.readStatus,
                    isRead: true,
                    readAt: now,
                },
            }))
        );

        setTimeout(async () => {
            try {
                const data = await markAllNotificationsAsRead(userData?.id);
                if (data?.success) {
                    showCustomToast("All notifications marked as read", "success");
                } else {
                    setLocalNotifications(previousNotifications);
                    showCustomToast("Error marking all notifications as read", "error");
                }
            } catch (error) {
                setLocalNotifications(previousNotifications);
                console.error('Error marking all as read', error);
                showCustomToast("Error marking all notifications as read", "error");
            } finally {
                setLoading(false);
            }
        }, 1500);
    };

    const hasNotifications = localNotifications?.length > 0;

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-transparent  h-screen w-screen pointer-events-none z-[9999]"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className={`bg-white notificationPopUp pointer-events-auto  sm:w-[480px] w-full  absolute top-16 right-2 max-w-[95%] sm:right-[80px] z-[10000]`}
            >
                <div className='flex items-center justify-between w-full'>
                    <span className='notificationsHeaderTitle'>Notifications</span>
                    <Close className='cursor-pointer' onClick={onClose} />
                </div>
                <div className={`notificationContainerWrapper ${hasNotifications ? '' : 'no-background'}`}>
                    <div
                        ref={containerRef}
                        className='notificationContainer custom-scrollbar'
                    >
                        {localNotifications?.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <NODATA className="w-32 h-32 opacity-60" />
                                <span className="noDataTitle">No notifications found</span>
                            </div>
                        ) : (
                            localNotifications?.map((notification, index) => (
                                <React.Fragment key={notification.id}>
                                    <NotificationItem
                                        notification={notification}
                                        userId={userData?.id}
                                        onMarkedAsRead={() => updateNotificationAsRead(notification.id)}
                                    />
                                    {index !== localNotifications.length - 1 && (
                                        <div className="my-3 h-px bg-[#F7F6F7] w-full"></div>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </div>
                </div>
                {localNotifications?.length > 0 &&
                    (
                        <button
                            onClick={handleMarkAllAsRead}
                            className={`w-auto markAllAsReadBtn ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={loading}
                        >
                            {loading ? (
                                <span>
                                    Marking as Read <span className="dotsLoading">...</span>
                                </span>
                            ) : 'Mark all as Read'}
                        </button>
                    )}
            </div>

        </div>,
        document.getElementById('notification-modal-root')
    )
}

export default NotificationModal
